import { normalizeRegionalBadge } from './normalizeRegionalBadge';

function stripEmoji(text) {
  return (text || '').replace(/[\p{Extended_Pictographic}\uFE0F]/gu, '');
}

function normalizeDashes(text) {
  return (text || '').replace(/[--]/g, '-');
}

function normalizeText(text) {
  return normalizeDashes(stripEmoji(text || '')).replace(/\s+/g, ' ').trim();
}

function textContent(node) {
  return normalizeText(node?.textContent || '');
}

function isGeneratedLine(text) {
  return normalizeText(text).toLowerCase().startsWith('generated:');
}

function standardizeZoneSeverityLabel(tone) {
  if (tone === 'warning') return 'Elevated';
  if (tone === 'good') return 'Managed';
  return 'Critical';
}

function classList(node) {
  return (node?.getAttribute('class') || '').split(/\s+/).filter(Boolean);
}

function byClass(node, className) {
  return node.querySelector(`.${className}`);
}

function severityFromClass(node) {
  const classes = classList(node);
  if (classes.includes('red')) return 'critical';
  if (classes.includes('orange')) return 'warning';
  if (classes.includes('purple')) return 'accent';
  if (classes.includes('green')) return 'good';
  const style = node.getAttribute('style') || '';
  if (style.includes('var(--green)')) return 'good';
  return 'neutral';
}

function toneFromBadgeClass(node) {
  const classes = classList(node);
  if (classes.includes('warn') || classes.includes('yellow')) return 'warning';
  if (classes.includes('ok') || classes.includes('green')) return 'good';
  return 'critical';
}

function parseCell(cell) {
  const badge = cell.querySelector('span');
  const strong = cell.querySelector('b, strong');
  const colSpan = cell.getAttribute('colspan');
  const children = Array.from(cell.children);
  const valueParts = [];

  if (normalizeText(cell.firstChild?.nodeValue)) {
    valueParts.push(normalizeText(cell.firstChild.nodeValue));
  }

  for (const child of children) {
    if (child.tagName === 'SPAN') {
      break;
    }

    const childText = textContent(child);
    if (childText) {
      valueParts.push(childText);
    }

    if (normalizeText(child.nextSibling?.nodeValue)) {
      valueParts.push(normalizeText(child.nextSibling.nodeValue));
    }
  }

  let value = normalizeText(valueParts.join(' '));
  if (!value) {
    value = textContent(cell);
    if (badge) {
      const badgeText = textContent(badge);
      if (value.endsWith(badgeText)) {
        value = normalizeText(value.slice(0, -badgeText.length));
      }
    }
  }

  if (!badge && !strong && !colSpan) {
    return value;
  }

  const output = { value };
  if (badge) {
    const tagClass = classList(badge).find((entry) => entry.startsWith('tag-'));
    const normalizedBadge = normalizeRegionalBadge(textContent(badge), tagClass ? tagClass.replace('tag-', '') : 'slate');
    output.badge = normalizedBadge.label;
    output.tone = normalizedBadge.tone;
    output.badgeKind = normalizedBadge.kind;
  }
  if (strong) {
    output.emphasis = true;
  }
  if (colSpan) {
    output.colSpan = Number(colSpan);
  }
  return output;
}

function parseTable(table) {
  const directRows = Array.from(table.children).filter((row) => row.tagName === 'TR');
  const bodyRows = table.tBodies.length ? Array.from(table.tBodies[0].rows) : [];
  const rows = directRows.length ? directRows : bodyRows;

  return {
    columns: Array.from(rows[0].querySelectorAll('th')).map(textContent),
    rows: rows.slice(1).map((row) => Array.from(row.querySelectorAll('td')).map(parseCell)),
  };
}

function parseInsight(card) {
  const classes = classList(card);
  let tone = 'critical';
  if (classes.includes('orange') || classes.includes('yellow')) tone = 'warning';
  if (classes.includes('green')) tone = 'good';
  if (classes.includes('purple')) tone = 'accent';

  const actionBox = byClass(card, 'action-box');
  const actionLabel = textContent(actionBox.querySelector('strong'));
  const actionText = normalizeText(actionBox.textContent.replace(actionLabel, ''));

  return {
    tone,
    title: textContent(byClass(card, 'insight-title')),
    exposure: textContent(byClass(card, 'exposure')),
    body: textContent(byClass(card, 'insight-text')),
    actionLabel,
    actionText,
  };
}

function parseBlock(node) {
  if (node.matches('table.mp-table')) {
    return { type: 'table', table: parseTable(node) };
  }

  if (node.matches('div.insight-card')) {
    return {
      type: 'insights',
      layout: 'stack',
      items: [parseInsight(node)],
      spaced: (node.getAttribute('style') || '').includes('margin-bottom'),
    };
  }

  if (node.matches('div.two-col')) {
    return {
      type: 'insights',
      layout: 'split',
      items: Array.from(node.children)
        .filter((card) => card.classList.contains('insight-card'))
        .map(parseInsight),
    };
  }

  if (node.matches('div.insight-grid')) {
    return {
      type: 'insights',
      layout: 'grid',
      items: Array.from(node.children)
        .filter((card) => card.classList.contains('insight-card'))
        .map(parseInsight),
    };
  }

  if (node.matches('p')) {
    return { type: 'note', text: textContent(node) };
  }

  return null;
}

function parseZoneSummary(documentNode) {
  return Array.from(documentNode.querySelectorAll('.kpi-section-label')).map((label) => ({
    title: textContent(label),
    items: Array.from(label.nextElementSibling.children)
      .filter((card) => card.classList.contains('kpi-card'))
      .map((card) => ({
      domainTag: textContent(byClass(card, 'domain-tag')),
      label: textContent(byClass(card, 'kpi-label')),
      value: textContent(byClass(card, 'kpi-val')),
      subtext: textContent(byClass(card, 'kpi-sub')),
      severity: severityFromClass(card),
      badge: {
        label: standardizeZoneSeverityLabel(toneFromBadgeClass(byClass(card, 'kpi-badge'))),
        tone: toneFromBadgeClass(byClass(card, 'kpi-badge')),
      },
      })),
  }));
}

function parseTabs(documentNode) {
  return Array.from(documentNode.querySelectorAll('.circle-tab')).map((tab) => {
    const id = classList(tab).find((entry) => ['HP', 'JA', 'KS', 'UW'].includes(entry));
    const label = normalizeText(
      Array.from(tab.childNodes)
        .filter((child) => child.nodeType === Node.TEXT_NODE)
        .map((child) => child.textContent)
        .join(' '),
    );

    return {
      id,
      label,
      summary: textContent(byClass(tab, 'tab-score')),
    };
  });
}

export function parseCooSourceHtml(sourceHtml) {
  const parser = new DOMParser();
  const documentNode = parser.parseFromString(sourceHtml, 'text/html');
  const titleNode = documentNode.querySelector('.header h1');
  const tabs = parseTabs(documentNode);

  return {
    meta: {
      title: normalizeText(
        Array.from(titleNode.childNodes)
          .filter((child) => child.nodeType === Node.TEXT_NODE)
          .map((child) => child.textContent)
          .join(' '),
      ),
      subtitle: textContent(titleNode.querySelector('span')),
      pills: Array.from(documentNode.querySelectorAll('.pill')).map(textContent),
      summaryLines: Array.from(documentNode.querySelectorAll('.header-right > div'))
        .map(textContent)
        .filter((line) => !isGeneratedLine(line)),
    },
    alert: {
      icon: textContent(documentNode.querySelector('.alert-banner span')),
      label: textContent(documentNode.querySelector('.alert-banner strong')),
      text: normalizeText(
        documentNode
          .querySelector('.alert-banner')
          .textContent.replace(textContent(documentNode.querySelector('.alert-banner span')), '')
          .replace(textContent(documentNode.querySelector('.alert-banner strong')), ''),
      ),
    },
    legend: {
      label: textContent(documentNode.querySelector('.domain-legend > span')),
      items: Array.from(documentNode.querySelectorAll('.domain-legend .dleg')).map((item) => ({
        label: textContent(item),
        color: item.querySelector('.dleg-dot')?.getAttribute('style')?.split(':')[1]?.trim() || '',
      })),
      note: textContent(documentNode.querySelector('.domain-legend > div:last-child')),
    },
    zoneSummarySections: parseZoneSummary(documentNode),
    circles: Array.from(documentNode.querySelectorAll('.circle-panel')).map((panel) => {
      const id = classList(panel).find((entry) => ['HP', 'JA', 'KS', 'UW'].includes(entry));
      const header = byClass(panel, 'circle-hdr');
      const headerText = header.querySelector('div');

      return {
        id,
        tabLabel: tabs.find((tab) => tab.id === id)?.label || '',
        tabSummary: tabs.find((tab) => tab.id === id)?.summary || '',
        heading: textContent(headerText.querySelector('h2')),
        mpSummary: textContent(headerText.querySelector('div')),
        scoreBadges: Array.from(header.querySelectorAll('.sbadge')).map((badge) => ({
          label: textContent(badge.querySelector('small')),
          value: normalizeText(textContent(badge).replace(textContent(badge.querySelector('small')), '')),
        })),
        sections: Array.from(panel.children)
          .filter((section) => section.classList.contains('domain-section'))
          .map((section, index) => {
          const sectionTitle = byClass(section, 'domain-section-hdr');
          return {
            id: `${id.toLowerCase()}-${index + 1}`,
            tone: classList(sectionTitle).find((entry) => entry !== 'domain-section-hdr') || 'tower',
            title: textContent(sectionTitle),
            blocks: Array.from(byClass(section, 'domain-section-body').children)
              .map(parseBlock)
              .filter(Boolean),
          };
        }),
      };
    }),
    footer: Array.from(documentNode.querySelector('.footer').childNodes)
      .map((node) => normalizeText(node.textContent))
      .filter(Boolean),
  };
}
